import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Country dial codes with per-country digit rules.
// Mexico-first because that's the primary target market. The rest of the list
// covers Latin America + Caribbean (the secondary focus) and a handful of
// other markets (US/CA/ES/GB/DE/FR) for inbound enterprise interest.
const COUNTRY_CODES = [
  // — Primary market —
  { code: "MX", dial: "+52",  name: "México",                       flag: "🇲🇽", minLen: 10, maxLen: 10 },

  // — Central America —
  { code: "BZ", dial: "+501", name: "Belize",                       flag: "🇧🇿", minLen: 7,  maxLen: 7  },
  { code: "CR", dial: "+506", name: "Costa Rica",                   flag: "🇨🇷", minLen: 8,  maxLen: 8  },
  { code: "SV", dial: "+503", name: "El Salvador",                  flag: "🇸🇻", minLen: 8,  maxLen: 8  },
  { code: "GT", dial: "+502", name: "Guatemala",                    flag: "🇬🇹", minLen: 8,  maxLen: 8  },
  { code: "HN", dial: "+504", name: "Honduras",                     flag: "🇭🇳", minLen: 8,  maxLen: 8  },
  { code: "NI", dial: "+505", name: "Nicaragua",                    flag: "🇳🇮", minLen: 8,  maxLen: 8  },
  { code: "PA", dial: "+507", name: "Panamá",                       flag: "🇵🇦", minLen: 7,  maxLen: 8  },

  // — South America —
  { code: "AR", dial: "+54",  name: "Argentina",                    flag: "🇦🇷", minLen: 10, maxLen: 11 },
  { code: "BO", dial: "+591", name: "Bolivia",                      flag: "🇧🇴", minLen: 8,  maxLen: 8  },
  { code: "BR", dial: "+55",  name: "Brasil",                       flag: "🇧🇷", minLen: 10, maxLen: 11 },
  { code: "CL", dial: "+56",  name: "Chile",                        flag: "🇨🇱", minLen: 9,  maxLen: 9  },
  { code: "CO", dial: "+57",  name: "Colombia",                     flag: "🇨🇴", minLen: 10, maxLen: 10 },
  { code: "EC", dial: "+593", name: "Ecuador",                      flag: "🇪🇨", minLen: 8,  maxLen: 9  },
  { code: "GY", dial: "+592", name: "Guyana",                       flag: "🇬🇾", minLen: 7,  maxLen: 7  },
  { code: "PY", dial: "+595", name: "Paraguay",                     flag: "🇵🇾", minLen: 9,  maxLen: 9  },
  { code: "PE", dial: "+51",  name: "Perú",                         flag: "🇵🇪", minLen: 9,  maxLen: 9  },
  { code: "SR", dial: "+597", name: "Suriname",                     flag: "🇸🇷", minLen: 6,  maxLen: 7  },
  { code: "UY", dial: "+598", name: "Uruguay",                      flag: "🇺🇾", minLen: 8,  maxLen: 8  },
  { code: "VE", dial: "+58",  name: "Venezuela",                    flag: "🇻🇪", minLen: 10, maxLen: 10 },

  // — Caribbean —
  { code: "AI", dial: "+1",   name: "Anguilla",                     flag: "🇦🇮", minLen: 10, maxLen: 10 },
  { code: "AG", dial: "+1",   name: "Antigua and Barbuda",          flag: "🇦🇬", minLen: 10, maxLen: 10 },
  { code: "AW", dial: "+297", name: "Aruba",                        flag: "🇦🇼", minLen: 7,  maxLen: 7  },
  { code: "BS", dial: "+1",   name: "Bahamas",                      flag: "🇧🇸", minLen: 10, maxLen: 10 },
  { code: "BB", dial: "+1",   name: "Barbados",                     flag: "🇧🇧", minLen: 10, maxLen: 10 },
  { code: "BM", dial: "+1",   name: "Bermuda",                      flag: "🇧🇲", minLen: 10, maxLen: 10 },
  { code: "VG", dial: "+1",   name: "British Virgin Islands",       flag: "🇻🇬", minLen: 10, maxLen: 10 },
  { code: "KY", dial: "+1",   name: "Cayman Islands",               flag: "🇰🇾", minLen: 10, maxLen: 10 },
  { code: "CU", dial: "+53",  name: "Cuba",                         flag: "🇨🇺", minLen: 8,  maxLen: 8  },
  { code: "CW", dial: "+599", name: "Curaçao",                      flag: "🇨🇼", minLen: 7,  maxLen: 8  },
  { code: "DM", dial: "+1",   name: "Dominica",                     flag: "🇩🇲", minLen: 10, maxLen: 10 },
  { code: "DO", dial: "+1",   name: "República Dominicana",         flag: "🇩🇴", minLen: 10, maxLen: 10 },
  { code: "GD", dial: "+1",   name: "Grenada",                      flag: "🇬🇩", minLen: 10, maxLen: 10 },
  { code: "GP", dial: "+590", name: "Guadeloupe",                   flag: "🇬🇵", minLen: 9,  maxLen: 9  },
  { code: "HT", dial: "+509", name: "Haiti",                        flag: "🇭🇹", minLen: 8,  maxLen: 8  },
  { code: "JM", dial: "+1",   name: "Jamaica",                      flag: "🇯🇲", minLen: 10, maxLen: 10 },
  { code: "MQ", dial: "+596", name: "Martinique",                   flag: "🇲🇶", minLen: 9,  maxLen: 9  },
  { code: "MS", dial: "+1",   name: "Montserrat",                   flag: "🇲🇸", minLen: 10, maxLen: 10 },
  { code: "PR", dial: "+1",   name: "Puerto Rico",                  flag: "🇵🇷", minLen: 10, maxLen: 10 },
  { code: "KN", dial: "+1",   name: "Saint Kitts and Nevis",        flag: "🇰🇳", minLen: 10, maxLen: 10 },
  { code: "LC", dial: "+1",   name: "Saint Lucia",                  flag: "🇱🇨", minLen: 10, maxLen: 10 },
  { code: "VC", dial: "+1",   name: "Saint Vincent & Grenadines",   flag: "🇻🇨", minLen: 10, maxLen: 10 },
  { code: "SX", dial: "+1",   name: "Sint Maarten",                 flag: "🇸🇽", minLen: 10, maxLen: 10 },
  { code: "TT", dial: "+1",   name: "Trinidad and Tobago",          flag: "🇹🇹", minLen: 10, maxLen: 10 },
  { code: "TC", dial: "+1",   name: "Turks and Caicos Islands",     flag: "🇹🇨", minLen: 10, maxLen: 10 },
  { code: "VI", dial: "+1",   name: "U.S. Virgin Islands",          flag: "🇻🇮", minLen: 10, maxLen: 10 },

  // — Other markets —
  { code: "US", dial: "+1",   name: "United States",                flag: "🇺🇸", minLen: 10, maxLen: 10 },
  { code: "CA", dial: "+1",   name: "Canada",                       flag: "🇨🇦", minLen: 10, maxLen: 10 },
  { code: "ES", dial: "+34",  name: "España",                       flag: "🇪🇸", minLen: 9,  maxLen: 9  },
  { code: "GB", dial: "+44",  name: "United Kingdom",               flag: "🇬🇧", minLen: 10, maxLen: 11 },
  { code: "DE", dial: "+49",  name: "Deutschland",                  flag: "🇩🇪", minLen: 10, maxLen: 11 },
  { code: "FR", dial: "+33",  name: "France",                       flag: "🇫🇷", minLen: 9,  maxLen: 9  }
];

// Match a domain. Accepts optional protocol + optional www + at least one dot
// followed by a 2+ char TLD. e.g. "company.com", "https://www.company.com.mx".
const DOMAIN_REGEX = /^(https?:\/\/)?(www\.)?([\w-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/i;

// --- Country typeahead combobox ----------------------------------------------
// Inline replacement for a native <select> so users can type "mex"/"+52"/"do"
// to jump to a country in the 52-item list. Mouse + keyboard accessible.
const CountryCombobox = ({ value, onChange, label, searchPlaceholder, styles }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const selected = COUNTRY_CODES.find((c) => c.code === value) || COUNTRY_CODES[0];

  const filtered = !query
    ? COUNTRY_CODES
    : COUNTRY_CODES.filter((c) => {
        const q = query.toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          c.code.toLowerCase().includes(q) ||
          c.dial.replace("+", "").includes(q.replace("+", ""))
        );
      });

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Focus the search input on open + reset state.
  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(Math.max(0, COUNTRY_CODES.findIndex((c) => c.code === value)));
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, value]);

  // Keep the highlighted option scrolled into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  const select = (country) => {
    onChange(country.code);
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter")   { e.preventDefault(); if (filtered[highlight]) select(filtered[highlight]); }
    else if (e.key === "Escape")  { e.preventDefault(); setOpen(false); }
  };

  return (
    <div ref={rootRef} style={styles.cbxRoot} data-testid="contact-input-country">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        style={styles.cbxTrigger}
        data-testid="country-combobox-trigger"
      >
        <span style={styles.cbxTriggerLabel}>
          <span style={styles.cbxFlag}>{selected.flag}</span>
          <span style={styles.cbxDial}>{selected.dial}</span>
          <span style={styles.cbxName}>{selected.name}</span>
        </span>
        <span aria-hidden="true" style={styles.cbxCaret}>▾</span>
      </button>

      {open && (
        <div style={styles.cbxPanel} role="dialog">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
            onKeyDown={onKey}
            placeholder={searchPlaceholder}
            style={styles.cbxSearch}
            data-testid="country-combobox-search"
            autoComplete="off"
          />
          <ul ref={listRef} role="listbox" style={styles.cbxList}>
            {filtered.length === 0 && <li style={styles.cbxEmpty}>—</li>}
            {filtered.map((c, idx) => {
              const active = idx === highlight;
              const isSel = c.code === value;
              return (
                <li
                  key={c.code}
                  data-idx={idx}
                  role="option"
                  aria-selected={isSel}
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => select(c)}
                  style={{
                    ...styles.cbxOption,
                    background: active ? "rgba(102,126,234,0.10)" : "transparent",
                    fontWeight: isSel ? 600 : 400
                  }}
                  data-testid={`country-option-${c.code}`}
                >
                  <span style={styles.cbxFlag}>{c.flag}</span>
                  <span style={styles.cbxDial}>{c.dial}</span>
                  <span style={styles.cbxName}>{c.name}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phoneNational: "",
    website: "",
    message: "",
    // Honeypot: hidden from real users via CSS; bots will fill it.
    company_fax: ""
  });
  const [countryCode, setCountryCode] = useState("MX");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Captured once on mount — used by the backend to detect bots that submit
  // the form in < 2 s.
  const formLoadedAt = useRef(Date.now());
  // Behavioral signal: bots almost never fire mousemove / keydown / touchstart.
  // We flip this flag the first time any real input arrives, then send it with
  // the submission. The backend silently drops payloads where it's false.
  const humanInteraction = useRef(false);

  useEffect(() => {
    const markHuman = () => { humanInteraction.current = true; };
    window.addEventListener("mousemove",  markHuman, { once: true, passive: true });
    window.addEventListener("keydown",    markHuman, { once: true, passive: true });
    window.addEventListener("touchstart", markHuman, { once: true, passive: true });
    window.addEventListener("pointerdown", markHuman, { once: true, passive: true });
    return () => {
      window.removeEventListener("mousemove",  markHuman);
      window.removeEventListener("keydown",    markHuman);
      window.removeEventListener("touchstart", markHuman);
      window.removeEventListener("pointerdown", markHuman);
    };
  }, []);

  const selectedCountry = COUNTRY_CODES.find((c) => c.code === countryCode) || COUNTRY_CODES[0];

  const handlePhoneChange = (e) => {
    // Strip everything that isn't a digit and cap at the country's max length.
    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, selectedCountry.maxLen);
    setFormData({ ...formData, phoneNational: digits });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation: phone length per country.
    const phoneLen = formData.phoneNational.length;
    if (phoneLen < selectedCountry.minLen || phoneLen > selectedCountry.maxLen) {
      const target = selectedCountry.minLen === selectedCountry.maxLen
        ? `${selectedCountry.minLen}`
        : `${selectedCountry.minLen}–${selectedCountry.maxLen}`;
      toast.error(t("contact.form.errors.phoneLength", { country: selectedCountry.name, digits: target }));
      return;
    }
    // Client-side validation: website must look like a domain.
    if (!DOMAIN_REGEX.test(formData.website.trim())) {
      toast.error(t("contact.form.errors.invalidWebsite"));
      return;
    }

    setIsSubmitting(true);
    const locale = (i18n.language || "es").toLowerCase().startsWith("en") ? "en" : "es";
    let source_path = "";
    let referrer = "";
    try {
      source_path = sessionStorage.getItem("lead_source_path") || "";
      referrer = sessionStorage.getItem("lead_initial_referrer") || "";
    } catch (_) { /* ignore */ }
    try {
      await axios.post(`${API_BASE}/api/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: `${selectedCountry.dial} ${formData.phoneNational}`,
        website: formData.website.trim(),
        message: formData.message,
        locale,
        source_path,
        referrer,
        honeypot: formData.company_fax,
        form_loaded_at: formLoadedAt.current,
        human_interaction: humanInteraction.current
      });
      setIsSubmitted(true);
      toast.success(t("contact.form.toastSuccess"));
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: "", email: "", company: "", phoneNational: "", website: "", message: "", company_fax: "" });
      }, 4000);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const fallback = t("contact.form.toastError");
      let msg = fallback;
      // Map known safeguard responses to localized copy.
      if (status === 400 && typeof detail === "string") {
        if (/disposable/i.test(detail)) {
          msg = t("contact.form.errors.disposableEmail");
        } else if (/website|domain/i.test(detail) && /placeholder|test\.com|example\.com/i.test(detail)) {
          msg = t("contact.form.errors.placeholderWebsite");
        } else if (/placeholder|test@|example\.com|real work email/i.test(detail)) {
          msg = t("contact.form.errors.placeholderEmail");
        } else {
          msg = t("contact.form.errors.workEmailRequired");
        }
      } else if (status === 409) {
        msg = t("contact.form.errors.duplicate");
      } else if (status === 429) {
        msg = t("contact.form.errors.rateLimited");
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        msg = detail[0].msg;
      } else if (typeof detail === "string") {
        msg = detail;
      }
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        path="/contacto"
        title={t("seo.contact.title")}
        description={t("seo.contact.description")}
        keywords={t("seo.contact.keywords")}
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "url": "https:// tarjetas.undostres.com.mx/contacto",
          "name": t("brand.name") + " Contact"
        }}
      />
      {/* Hero */}
      <section className="hero" style={styles.hero}>
        <div className="container">
          <div className="heroInner" style={styles.heroInner}>
            <div className="eyebrow" style={styles.eyebrow}>{t("contact.hero.eyebrow")}</div>
            <h1 className="heroTitle" style={styles.heroTitle}>
              {t("contact.hero.titleLine1")}<br />{t("contact.hero.titleLine2")}
            </h1>
            <p className="heroSubtitle" style={styles.heroSubtitle}>{t("contact.hero.subtitle")}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="contentSection" style={styles.contentSection}>
        <div className="container">
          <div className="formOnly" style={styles.formOnly}>
            <div className="formSide" style={styles.formSide}>
              {isSubmitted ? (
                <div className="successBox" style={styles.successBox} data-testid="contact-form-success">
                  <CheckCircle2 size={56} color="#f5576c" />
                  <h3 className="successTitle" style={styles.successTitle}>{t("contact.form.successTitle")}</h3>
                  <p className="successText" style={styles.successText}>{t("contact.form.successText")}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="form" style={styles.form} data-testid="contact-form">
                  <div className="row" style={styles.row}>
                    <div className="field" style={styles.field}>
                      <label className="label" style={styles.label}>{t("contact.form.name")}</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="input" style={styles.input}
                        placeholder={t("contact.form.namePlaceholder")}
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div className="field" style={styles.field}>
                      <label className="label" style={styles.label}>{t("contact.form.email")}</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="input" style={styles.input}
                        placeholder={t("contact.form.emailPlaceholder")}
                        data-testid="contact-input-email"
                      />
                      <div style={styles.hint} data-testid="contact-email-hint">{t("contact.form.emailHint")}</div>
                    </div>
                  </div>

                  {/* Honeypot — hidden from real users via CSS; bots will fill it. */}
                  <div className="hp-field" aria-hidden="true" style={styles.honeypot}>
                    <label htmlFor="company_fax">Company fax</label>
                    <input
                      type="text"
                      id="company_fax"
                      name="company_fax"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.company_fax}
                      onChange={handleChange}
                      data-testid="contact-input-honeypot"
                    />
                  </div>

                  <div className="row" style={styles.row}>
                    <div className="field" style={styles.field}>
                      <label className="label" style={styles.label}>{t("contact.form.company")}</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="input" style={styles.input}
                        placeholder={t("contact.form.companyPlaceholder")}
                        data-testid="contact-input-company"
                      />
                    </div>
                    <div className="field" style={styles.field}>
                      <label className="label" style={styles.label}>{t("contact.form.website")}</label>
                      <input
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        required
                        className="input" style={styles.input}
                        placeholder={t("contact.form.websitePlaceholder")}
                        data-testid="contact-input-website"
                      />
                    </div>
                  </div>

                  <div className="field" style={styles.field}>
                    <label className="label" style={styles.label}>{t("contact.form.phone")}</label>
                    <div style={styles.phoneRow}>
                      <CountryCombobox
                        value={countryCode}
                        label={t("contact.form.country")}
                        searchPlaceholder={t("contact.form.searchCountry")}
                        styles={styles}
                        onChange={(code) => {
                          setCountryCode(code);
                          // Re-clamp the national number to the new country's max length.
                          const newCountry = COUNTRY_CODES.find((c) => c.code === code);
                          if (newCountry && formData.phoneNational.length > newCountry.maxLen) {
                            setFormData((prev) => ({ ...prev, phoneNational: prev.phoneNational.slice(0, newCountry.maxLen) }));
                          }
                        }}
                      />
                      <input
                        type="tel"
                        name="phoneNational"
                        value={formData.phoneNational}
                        onChange={handlePhoneChange}
                        required
                        inputMode="numeric"
                        pattern="\d*"
                        minLength={selectedCountry.minLen}
                        maxLength={selectedCountry.maxLen}
                        className="input" style={styles.phoneInput}
                        placeholder={selectedCountry.minLen === selectedCountry.maxLen
                          ? `${selectedCountry.minLen} ${t("contact.form.digitsShort")}`
                          : `${selectedCountry.minLen}–${selectedCountry.maxLen} ${t("contact.form.digitsShort")}`}
                        data-testid="contact-input-phone"
                      />
                    </div>
                  </div>

                  <div className="field" style={styles.field}>
                    <label className="label" style={styles.label}>{t("contact.form.message")}</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      style={{ ...styles.input, resize: "vertical", minHeight: 130 }}
                      placeholder={t("contact.form.messagePlaceholder")}
                      data-testid="contact-input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary submit"
                    style={styles.submit}
                    disabled={isSubmitting}
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? t("contact.form.submitting") : (
                      <>
                        {t("contact.form.submit")}
                        <Send size={18} style={{ marginLeft: 8 }} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  hero: {
    padding: "160px 20px 60px",
    background: "#ffffff"
  },
  heroInner: {
    maxWidth: "900px",
    margin: "0 auto"
  },
  eyebrow: {
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#f5576c",
    marginBottom: "20px"
  },
  heroTitle: {
    fontSize: "60px",
    fontWeight: 700,
    lineHeight: 1.05,
    letterSpacing: "-0.03em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "22px"
  },
  heroSubtitle: {
    fontSize: "19px",
    lineHeight: 1.6,
    color: "#4a5568",
    maxWidth: "720px"
  },
  contentSection: {
    padding: "40px 20px 120px",
    background: "#ffffff"
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1.2fr",
    gap: "60px",
    maxWidth: "1200px",
    margin: "0 auto",
    alignItems: "start"
  },
  formOnly: {
    maxWidth: "760px",
    margin: "0 auto"
  },
  infoSide: {
    padding: "40px",
    background: "#f8f9fc",
    borderRadius: "16px",
    border: "1px solid rgba(0,0,0,0.06)"
  },
  infoTitle: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a202c",
    marginBottom: "10px",
    letterSpacing: "-0.01em"
  },
  infoText: {
    fontSize: "15px",
    color: "#4a5568",
    lineHeight: 1.6,
    marginBottom: "28px"
  },
  contactList: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    marginBottom: "28px"
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    gap: "14px"
  },
  iconCircle: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  contactLabel: {
    fontSize: "12px",
    color: "#718096",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "2px"
  },
  contactValue: {
    fontSize: "15px",
    color: "#1a202c",
    fontWeight: 500,
    textDecoration: "none"
  },
  note: {
    padding: "18px 20px",
    borderRadius: "12px",
    background: "linear-gradient(180deg, #f093fb 0%, #f5576c 55%, #fda085 100%)",
    color: "#ffffff"
  },
  noteTitle: {
    fontSize: "14px",
    fontWeight: 700,
    marginBottom: "4px"
  },
  noteText: {
    fontSize: "14px",
    opacity: 0.92
  },
  formSide: {
    padding: "40px",
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.04)"
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px"
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px"
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  label: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#2d3748",
    letterSpacing: "-0.005em"
  },
  hint: {
    fontSize: "12px",
    color: "#94a3b8",
    marginTop: "2px",
    fontWeight: 400,
    lineHeight: 1.4
  },
  honeypot: {
    position: "absolute",
    left: "-10000px",
    width: "1px",
    height: "1px",
    overflow: "hidden",
    opacity: 0,
    pointerEvents: "none"
  },
  phoneRow: {
    display: "grid",
    gridTemplateColumns: "260px 1fr",
    gap: "12px",
    alignItems: "stretch"
  },
  // --- CountryCombobox ----------------------------------------------------
  cbxRoot: {
    position: "relative",
    width: "100%",
    minWidth: 0
  },
  cbxTrigger: {
    width: "100%",
    height: "100%",
    minHeight: 46,
    padding: "10px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    fontSize: "14px",
    background: "#fff",
    color: "#1a202c",
    fontWeight: 500,
    cursor: "pointer",
    outline: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8
  },
  cbxTriggerLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  cbxFlag: { fontSize: 16, lineHeight: 1, display: "inline-block" },
  cbxDial: { fontWeight: 600, color: "#475569", fontVariantNumeric: "tabular-nums" },
  cbxName: { color: "#1a202c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  cbxCaret: { color: "#94a3b8", fontSize: 12 },
  cbxPanel: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    boxShadow: "0 24px 48px -16px rgba(15,23,42,0.18), 0 0 1px rgba(15,23,42,0.04)",
    zIndex: 50,
    overflow: "hidden",
    minWidth: 280
  },
  cbxSearch: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid #f1f5f9",
    padding: "12px 14px",
    fontSize: 14,
    outline: "none",
    color: "#1a202c",
    background: "#fff"
  },
  cbxList: {
    listStyle: "none",
    margin: 0,
    padding: 6,
    maxHeight: 280,
    overflowY: "auto"
  },
  cbxOption: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    color: "#1a202c"
  },
  cbxEmpty: { padding: "10px 12px", color: "#94a3b8", fontSize: 13 },
  phoneCountry: {
    padding: "12px 14px",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    fontSize: "14px",
    background: "#fff",
    color: "#1a202c",
    fontWeight: 500,
    cursor: "pointer",
    outline: "none",
    width: "100%",
    minWidth: 0,
    appearance: "auto"
  },
  phoneInput: {
    padding: "12px 16px",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    fontSize: "15px",
    background: "#fff",
    color: "#1a202c",
    outline: "none",
    fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
    letterSpacing: "0.06em",
    width: "100%",
    minWidth: 0
  },
  input: {
    padding: "12px 14px",
    fontSize: "15px",
    color: "#1a202c",
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: "10px",
    outline: "none",
    fontFamily: "inherit",
    transition: "border-color 0.2s, box-shadow 0.2s"
  },
  submit: {
    marginTop: "8px",
    fontSize: "16px",
    padding: "14px 28px"
  },
  successBox: {
    padding: "60px 20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px"
  },
  successTitle: {
    fontSize: "26px",
    fontWeight: 700,
    color: "#1a202c",
    letterSpacing: "-0.01em"
  },
  successText: {
    fontSize: "16px",
    color: "#4a5568",
    lineHeight: 1.6,
    maxWidth: "420px"
  }
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  [data-testid="contact-page"] input:focus,
  [data-testid="contact-page"] textarea:focus {
    border-color: #667eea !important;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15) !important;
  }
  @media (max-width: 1024px) {
    [data-testid="contact-page"] div[style*="grid-template-columns: 1fr 1.2fr"] { grid-template-columns: 1fr !important; }
    [data-testid="contact-page"] h1 { font-size: 44px !important; }
  }
  @media (max-width: 640px) {
    [data-testid="contact-page"] section:first-child { padding: 120px 16px 40px !important; }
    [data-testid="contact-page"] h1 { font-size: 34px !important; }
    [data-testid="contact-page"] div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
    [data-testid="contact-page"] div[style*="grid-template-columns: 260px 1fr"] { grid-template-columns: 1fr !important; }
    [data-testid="contact-page"] div[style*="padding: 40px"] { padding: 26px !important; }
  }
`;
document.head.appendChild(styleSheet);

export default ContactPage;
